import { useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Package,
  Plus,
  Trash2,
  Minus,
  Banknote,
  Percent,
  ChevronsUpDown,
  ExternalLink,
  Check,
  Loader2,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { normalizePriceOrZero } from "@/lib/price";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function lineTotal(row: {
  link: { quantity: number; unitPrice: string; discountPct: number };
}): number {
  return (
    Number(row.link.quantity ?? 1) *
    Number(row.link.unitPrice ?? 0) *
    ((100 - Number(row.link.discountPct ?? 0)) / 100)
  );
}

// ─── Product picker (combobox) ───────────────────────────────────────────────
function ProductPicker({
  onPick,
}: {
  onPick: (row: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = trpc.crm.products.list.useQuery(
    { search: query || undefined, limit: 20, withOffers: true },
    { enabled: open },
  );
  const list = search.data?.rows ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted/40 transition"
        >
          <span className="text-muted-foreground flex items-center gap-2">
            <Package className="h-4 w-4" />
            Buscar produto…
          </span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Nome ou SKU..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {search.isFetching ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                Buscando…
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Nenhum produto encontrado.
                    <div className="mt-2">
                      <Link
                        href="/crm/products"
                        className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                      >
                        Abrir catálogo
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </CommandEmpty>
                <CommandGroup>
                  {list.map((row: any) => (
                    <CommandItem
                      key={row.product.id}
                      onSelect={() => {
                        onPick(row);
                        setOpen(false);
                        setQuery("");
                      }}
                      className="flex items-center gap-2"
                    >
                      <div className="h-8 w-8 rounded-md bg-muted overflow-hidden shrink-0">
                        {row.product.imageUrl ? (
                          <img
                            src={row.product.imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <Package className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {row.product.name}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          {row.product.sku && (
                            <span className="font-mono">{row.product.sku}</span>
                          )}
                          <span className="tabular-nums">
                            {row.offerCount > 0
                              ? `a partir de ${formatBRL(row.offerMinPrice)}`
                              : formatBRL(row.product.price)}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Add Product Sheet ───────────────────────────────────────────────────────
function AddProductSheet({
  open,
  onOpenChange,
  dealId,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  dealId: number;
  onAdded?: () => void;
}) {
  const [product, setProduct] = useState<any | null>(null);
  const [offers, setOffers] = useState<any[]>([]);
  const [offerId, setOfferId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<string>("");
  const [discountPct, setDiscountPct] = useState<number>(0);
  const [notes, setNotes] = useState("");

  const utils = trpc.useUtils();
  const add = trpc.crm.dealProducts.add.useMutation({
    onSuccess: () => {
      toast.success("Produto vinculado");
      utils.crm.dealProducts.list.invalidate({ dealId });
      utils.crm.dealProducts.total.invalidate({ dealId });
      onOpenChange(false);
      reset();
      onAdded?.();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  function reset() {
    setProduct(null);
    setOffers([]);
    setOfferId(null);
    setQuantity(1);
    setUnitPrice("");
    setDiscountPct(0);
    setNotes("");
  }

  function pickProduct(row: any) {
    const list = row.offers ?? [];
    setProduct(row.product);
    setOffers(list);
    if (list.length === 1) {
      // Uma oferta só não é escolha — seria um passo a mais para uma única
      // opção. Já seleciona e traz o preço dela.
      setOfferId(list[0].id);
      setUnitPrice(String(list[0].price ?? "0"));
    } else {
      // 0 ofertas: comportamento anterior (preço sugerido do produto).
      // 2+: o usuário precisa escolher, e aí o preço vem junto.
      setOfferId(null);
      setUnitPrice(String(row.product.price ?? "0"));
    }
  }

  function pickOffer(o: any) {
    setOfferId(o.id);
    setUnitPrice(String(o.price ?? "0"));
  }

  const total =
    quantity * Number(unitPrice || 0) * ((100 - discountPct) / 100);

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <SheetContent className="sm:max-w-[440px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>Vincular produto</SheetTitle>
              <SheetDescription>
                Registre o que está sendo negociado e o preço praticado.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <Label className="text-xs">Produto</Label>
            <div className="mt-1">
              {product ? (
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-2.5">
                  <div className="h-10 w-10 rounded-md bg-background overflow-hidden shrink-0 border">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{product.name}</div>
                    {product.sku && (
                      <div className="text-xs text-muted-foreground font-mono">
                        {product.sku}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setProduct(null)}
                  >
                    Trocar
                  </Button>
                </div>
              ) : (
                <ProductPicker onPick={pickProduct} />
              )}
            </div>
          </div>

          {/* Escolha da oferta. Só aparece com 2+ — com uma só, pickProduct já
              escolheu; com nenhuma, vale o preço sugerido do produto. */}
          {product && offers.length > 1 && (
            <div>
              <Label className="text-xs">Oferta</Label>
              <div className="mt-1 space-y-1.5">
                {offers.map((o: any) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => pickOffer(o)}
                    className={`w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition ${
                      offerId === o.id
                        ? "border-primary ring-1 ring-primary bg-primary/5"
                        : "hover:bg-muted/40"
                    }`}
                  >
                    <span className="truncate">{o.name}</span>
                    <span className="tabular-nums shrink-0">{formatBRL(o.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {product && (
            <>
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dp-qty" className="text-xs">Quantidade</Label>
                  <div className="mt-1 flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 shrink-0"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      id="dp-qty"
                      type="number"
                      min={1}
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(Math.max(1, Number(e.target.value) || 1))
                      }
                      className="text-center"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 shrink-0"
                      onClick={() => setQuantity((q) => q + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="dp-price" className="text-xs">Preço unitário</Label>
                  <div className="relative mt-1">
                    <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input
                      id="dp-price"
                      type="number"
                      step="0.01"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      className="pl-8 tabular-nums"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <Percent className="h-3 w-3" /> Desconto
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {discountPct}%
                  </span>
                </Label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={discountPct}
                  onChange={(e) => setDiscountPct(Number(e.target.value))}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              <div>
                <Label htmlFor="dp-notes" className="text-xs">Observação</Label>
                <Input
                  id="dp-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Opcional"
                  className="mt-1"
                />
              </div>

              <Card className="bg-primary/5 border-primary/30">
                <CardContent className="p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Total desta linha
                  </span>
                  <span className="text-lg font-bold tabular-nums">
                    {formatBRL(total)}
                  </span>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <SheetFooter className="border-t px-6 py-4 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!product) return toast.error("Selecione um produto");
              // Sem isto, a linha gravaria o preço-base em silêncio e o
              // relatório "Presencial x Online" ficaria cego.
              if (offers.length > 1 && !offerId)
                return toast.error("Selecione a oferta");
              add.mutate({
                dealId,
                productId: product.id,
                offerId,
                quantity,
                unitPrice: normalizePriceOrZero(unitPrice),
                discountPct,
                notes: notes || null,
              });
            }}
            disabled={add.isPending || !product}
          >
            {add.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Vincular
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Row (linha de produto vinculado) ────────────────────────────────────────
function DealProductRow({
  row,
  canManage,
  onUpdate,
  onRemove,
}: {
  row: any;
  canManage: boolean;
  onUpdate: (patch: any) => void;
  onRemove: () => void;
}) {
  const [qty, setQty] = useState(Number(row.link.quantity ?? 1));
  const [price, setPrice] = useState(String(row.link.unitPrice ?? "0"));
  const [disc, setDisc] = useState(Number(row.link.discountPct ?? 0));
  const dirty =
    qty !== Number(row.link.quantity) ||
    price !== String(row.link.unitPrice) ||
    disc !== Number(row.link.discountPct);
  const total = qty * Number(price || 0) * ((100 - disc) / 100);

  return (
    <div className="rounded-lg border bg-card p-3 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-md bg-muted overflow-hidden shrink-0 border">
          {row.product?.imageUrl ? (
            <img
              src={row.product.imageUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Link
            href={`/crm/products/${row.product?.id}`}
            className="text-sm font-semibold hover:text-primary line-clamp-1"
          >
            {row.product?.name ?? "Produto"}
          </Link>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            {/* Oferta viva quando existe; senão o snapshot gravado na linha —
                é o que preserva "Online" mesmo se a oferta for apagada. */}
            {(row.offer?.name ?? row.link.offerName) && (
              <Badge variant="outline" className="text-[10px] h-4">
                {row.offer?.name ?? row.link.offerName}
              </Badge>
            )}
            {row.product?.sku && (
              <span className="font-mono flex items-center gap-1">
                <Hash className="h-3 w-3" />
                {row.product.sku}
              </span>
            )}
            {row.category?.name && (
              <Badge
                variant="secondary"
                className="text-[10px] h-4"
                style={{
                  backgroundColor: row.category.color
                    ? `${row.category.color}20`
                    : undefined,
                  color: row.category.color ?? undefined,
                }}
              >
                {row.category.name}
              </Badge>
            )}
          </div>
        </div>
        {canManage && (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
            onClick={onRemove}
            title="Remover"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Qtd</Label>
          <Input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
            disabled={!canManage}
            className="h-8 text-sm"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-muted-foreground">
            Preço unitário
          </Label>
          <Input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={!canManage}
            className="h-8 text-sm tabular-nums"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">
            Desc %
          </Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={disc}
            onChange={(e) =>
              setDisc(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
            }
            disabled={!canManage}
            className="h-8 text-sm"
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t">
        <span className="text-xs text-muted-foreground">Total da linha</span>
        <span className="text-base font-bold tabular-nums">{formatBRL(total)}</span>
      </div>

      {canManage && dirty && (
        <Button
          size="sm"
          className="w-full"
          onClick={() =>
            onUpdate({
              quantity: qty,
              unitPrice: price,
              discountPct: disc,
            })
          }
        >
          <Check className="h-3.5 w-3.5 mr-1.5" />
          Salvar alterações
        </Button>
      )}
    </div>
  );
}

// ─── Panel ───────────────────────────────────────────────────────────────────
export default function DealProductsPanel({ dealId }: { dealId: number }) {
  const { can } = usePermissions();
  const canManage = can("crm:deal_products_manage");
  const [sheetOpen, setSheetOpen] = useState(false);

  const listQuery = trpc.crm.dealProducts.list.useQuery({ dealId });
  const totalQuery = trpc.crm.dealProducts.total.useQuery({ dealId });

  const utils = trpc.useUtils();
  const update = trpc.crm.dealProducts.update.useMutation({
    onSuccess: () => {
      toast.success("Atualizado");
      utils.crm.dealProducts.list.invalidate({ dealId });
      utils.crm.dealProducts.total.invalidate({ dealId });
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const remove = trpc.crm.dealProducts.remove.useMutation({
    onSuccess: () => {
      utils.crm.dealProducts.list.invalidate({ dealId });
      utils.crm.dealProducts.total.invalidate({ dealId });
    },
  });

  const rows = listQuery.data ?? [];
  const total = Number(totalQuery.data ?? 0);
  const count = useMemo(
    () => rows.reduce((acc: number, r: any) => acc + Number(r.link.quantity ?? 0), 0),
    [rows],
  );

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Produtos
            {rows.length > 0 && <Badge variant="secondary">{rows.length}</Badge>}
          </CardTitle>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Vincular
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {listQuery.isLoading ? (
            <div className="h-24 bg-muted rounded animate-pulse" />
          ) : rows.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-10 w-10 mx-auto mb-2 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground mb-3">
                Nenhum produto vinculado.
              </p>
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Vincular produto
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row: any) => (
                <DealProductRow
                  key={row.link.id}
                  row={row}
                  canManage={canManage}
                  onUpdate={(patch) => update.mutate({ id: row.link.id, ...patch })}
                  onRemove={() => remove.mutate({ id: row.link.id })}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">
                Total ({count} {count === 1 ? "item" : "itens"})
              </div>
              <div className="text-2xl font-bold tabular-nums">{formatBRL(total)}</div>
            </div>
            {count > 0 && (
              <div className="text-right border-l border-primary/20 pl-4">
                <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Ticket médio / item
                </div>
                <div className="text-base font-semibold tabular-nums">
                  {formatBRL(total / count)}
                </div>
              </div>
            )}
            <Banknote className="h-8 w-8 text-primary opacity-60 shrink-0" />
          </CardContent>
        </Card>
      )}

      <AddProductSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        dealId={dealId}
      />
    </div>
  );
}
